/**
 * Pure validation helpers for remotely-edited EN/AR copy.
 *
 * Hand-copied ES-module port of skilzBackend/app/services/translationValidation.js
 * (separate repos, no shared package). The function bodies are kept identical so
 * the admin's live counters match what the server enforces; both repos run the
 * same fixture table (src/utils/__tests__/translationValidationCases.json here,
 * tests/fixtures/translationValidationCases.json in the backend). Any change
 * there must be mirrored here.
 */

const LOCALES = ['en', 'ar']
const TIERS = ['tight', 'normal', 'long']

const MIN_MAX_LENGTH = 4
const MAX_MAX_LENGTH = 2000
const AR_TIGHT_EXTRA = 2
const NEAR_LIMIT_RATIO = 0.9

// Integer percentages so Math.ceil never sees float noise (e.g. 60 * 1.15).
const TIER_RULES = {
  tight: { pct: 115, add: 2 },
  normal: { pct: 130, add: 5 },
  long: { pct: 150, add: 0 }
}

const PLACEHOLDER_RE = /\{([A-Za-z0-9_]+)\}/g
const ARABIC_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/
// C0 controls except \n (U+000A), DEL, and C1 controls.
const CONTROL_RE = /[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/

const codeUnitCompare = (a, b) => (a < b ? -1 : a > b ? 1 : 0)

/** Length in code points, so emoji / surrogate pairs count once. */
const codePointLength = str => [...String(str)].length

/** Sorted (code-unit order), de-duplicated `{token}` names found in `str`. */
const extractPlaceholders = str => {
  if (typeof str !== 'string' || !str) return []
  const found = new Set()
  let m
  PLACEHOLDER_RE.lastIndex = 0
  while ((m = PLACEHOLDER_RE.exec(str)) !== null) found.add(m[1])
  return [...found].sort(codeUnitCompare)
}

/**
 * maxLength for one locale, derived from that locale's bundled length.
 *
 *   tight:  ceil(L × 1.15) + 2
 *   normal: ceil(L × 1.30) + 5
 *   long:   ceil(L × 1.50)
 *   floor 4, cap 2000
 *
 * For AR in the tight tier pass `{ locale: 'ar', enBundledLen }` to clamp the
 * result to ≤ (EN tight max + 2), since Arabic glyphs render wider.
 */
const computeMaxLength = (bundledLen, tier = 'normal', opts = {}) => {
  const rule = TIER_RULES[tier] || TIER_RULES.normal
  const L = Math.max(0, Math.floor(Number(bundledLen) || 0))
  let max = Math.ceil((L * rule.pct) / 100) + rule.add
  max = Math.min(MAX_MAX_LENGTH, Math.max(MIN_MAX_LENGTH, max))

  if (
    tier === 'tight' &&
    opts.locale === 'ar' &&
    opts.enBundledLen !== undefined &&
    opts.enBundledLen !== null
  ) {
    const enMax = computeMaxLength(opts.enBundledLen, 'tight')
    max = Math.min(max, enMax + AR_TIGHT_EXTRA)
  }
  return max
}

const issue = (code, message, extra) => (extra ? { code, message, ...extra } : { code, message })

/**
 * Validate one locale value.
 *
 * @param {Object} input
 * @param {string} input.value        - candidate value (untrimmed)
 * @param {'en'|'ar'} input.locale
 * @param {number} input.maxLength    - that locale's limit
 * @param {string[]} [input.placeholders] - required placeholder set (from bundled)
 * @param {string} [input.tier]      - accepted for parity; limits are already in maxLength
 * @param {string|null} [input.bundled] - bundled value; `\n` is only allowed if it has one
 * @returns {{ ok: boolean, value: string, length: number, errors: Object[], warnings: Object[] }}
 *   `value` is the trimmed value that should be stored.
 */
const validateValue = ({ value, locale, maxLength, placeholders = [], bundled = null } = {}) => {
  const errors = []
  const warnings = []

  if (typeof value !== 'string') {
    errors.push(issue('INVALID_TYPE', 'Value must be a string'))
    return { ok: false, value: '', length: 0, errors, warnings }
  }
  if (!LOCALES.includes(locale)) {
    errors.push(issue('INVALID_LOCALE', `Unknown locale "${locale}"`))
  }

  const trimmed = value.trim()
  const length = codePointLength(trimmed)

  if (trimmed !== value && trimmed.length > 0) {
    warnings.push(issue('WHITESPACE_TRIMMED', 'Leading/trailing whitespace was removed'))
  }

  if (!trimmed) {
    errors.push(issue('EMPTY', 'Value cannot be empty'))
    return { ok: false, value: trimmed, length, errors, warnings }
  }

  const limit = Number(maxLength)
  if (Number.isFinite(limit) && limit > 0) {
    if (length > limit) {
      errors.push(issue('OVER_LIMIT', `Too long: ${length}/${limit}`, { length, maxLength: limit }))
    } else if (length > NEAR_LIMIT_RATIO * limit) {
      warnings.push(issue('NEAR_LIMIT', `Close to the limit: ${length}/${limit}`, { length, maxLength: limit }))
    }
  }

  const expected = [...new Set(placeholders || [])].sort(codeUnitCompare)
  const actual = extractPlaceholders(trimmed)
  const missing = expected.filter(p => !actual.includes(p))
  const extra = actual.filter(p => !expected.includes(p))
  if (missing.length || extra.length) {
    const parts = []
    if (missing.length) parts.push(`missing ${missing.map(p => `{${p}}`).join(', ')}`)
    if (extra.length) parts.push(`unexpected ${extra.map(p => `{${p}}`).join(', ')}`)
    errors.push(issue('PLACEHOLDER_MISMATCH', `Placeholders: ${parts.join('; ')}`, { missing, extra }))
  }

  if (CONTROL_RE.test(trimmed)) {
    errors.push(issue('CONTROL_CHARS', 'Value contains control characters'))
  }

  if (trimmed.includes('\n') && !(typeof bundled === 'string' && bundled.includes('\n'))) {
    errors.push(issue('NEWLINE_NOT_ALLOWED', 'Line breaks are not allowed for this string'))
  }

  const hasArabic = ARABIC_RE.test(trimmed)
  if (locale === 'ar' && !hasArabic) {
    warnings.push(issue('AR_NO_ARABIC', 'Arabic value contains no Arabic characters'))
  }
  if (locale === 'en' && hasArabic) {
    warnings.push(issue('EN_HAS_ARABIC', 'English value contains Arabic characters'))
  }

  return { ok: errors.length === 0, value: trimmed, length, errors, warnings }
}

export {
  LOCALES,
  TIERS,
  MIN_MAX_LENGTH,
  MAX_MAX_LENGTH,
  NEAR_LIMIT_RATIO,
  ARABIC_RE,
  codeUnitCompare,
  codePointLength,
  extractPlaceholders,
  computeMaxLength,
  validateValue
}
